/**
 * Buster Bucks on the client: the guest wallet, and the arithmetic of a seat.
 *
 * ================================ TWO WALLETS ==================================
 * A signed-in player's balance is server-side truth and this module never touches it.
 * A signed-OUT player still gets to sit down — the saloon's doors open for everyone
 * and the gate is at the seat, not at the door — so they get a DEVICE wallet: the same
 * numbers, the same floor, the same Barkeep, kept in `localStorage` and worth nothing
 * anywhere else.
 *
 * ------------------------- WHY IT DOES NOT MIGRATE -----------------------------
 * The obvious thing is to add the device balance to the server grant on first
 * sign-in. It is also free money on every sign-up: clear the storage, play a guest
 * session up to 40,000, sign up, and repeat with a new address. There is nothing to
 * steal — the Bucks are free and refill on their own — but a balance anybody can
 * manufacture is a balance no leaderboard can be built on, and "net since Monday" is
 * a board the master plan already promises.
 *
 * So the rule is simple and it is stated on screen: signing in starts you at 5,000,
 * and the device wallet is cleared. Nobody loses anything real, because there is
 * nothing real to lose.
 *
 * Kept free of React and of the DOM beyond `localStorage`, both of which are wrapped,
 * so `tests/wallet-client.test.ts` can import it under Node.
 * ===============================================================================
 */

/**
 * The three numbers, mirrored from `app/src/lib/wallet.ts`.
 *
 * They are duplicated because the Worker and the browser share no module — the web
 * app's aliases reach `@engine`, `@pipeline` and `@config`, and `src/` is the Worker.
 * `tests/wallet-client.test.ts` reads both files and fails if they ever drift, which
 * is the same trick `tests/mark.test.ts` plays on the two favicon copies.
 */
export const STARTING_GRANT = 5000;
export const STAKE_FLOOR = 500;
export const STAKE_TO = 1000;

export type GuestReason = "grant" | "buyin" | "settle" | "stake";

export interface GuestEntry {
  delta: number;
  balanceAfter: number;
  reason: GuestReason;
  at: number;
}

export interface GuestWallet {
  balance: number;
  /** The last twenty, same as the server's endpoint returns. */
  ledger: GuestEntry[];
}

const KEY = "bb.guest-wallet";
const KEEP = 20;

/**
 * Read it, or mint one.
 *
 * Wrapped on both sides, like `loadMoneyMode`: storage throws outright in a private
 * window on some browsers and anywhere site data is blocked, and a saloon that will
 * not render because it could not read a play-money balance is a far worse bug than
 * one that starts you at 5,000 again.
 */
export function readGuestWallet(): GuestWallet {
  const fresh = (): GuestWallet => ({
    balance: STARTING_GRANT,
    ledger: [{ delta: STARTING_GRANT, balanceAfter: STARTING_GRANT, reason: "grant", at: Date.now() }],
  });
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return fresh();
    const parsed = JSON.parse(raw) as Partial<GuestWallet>;
    const balance = typeof parsed.balance === "number" && Number.isFinite(parsed.balance)
      ? Math.max(0, Math.trunc(parsed.balance)) : STARTING_GRANT;
    const ledger = Array.isArray(parsed.ledger) ? parsed.ledger.slice(0, KEEP) : [];
    return { balance, ledger };
  } catch {
    return fresh();
  }
}

function write(w: GuestWallet): void {
  try { localStorage.setItem(KEY, JSON.stringify(w)); } catch { /* not worth a broken saloon */ }
}

/**
 * One movement, with the server's rules run locally: whole Bucks, never negative, and
 * a refusal writes nothing. Returns null when it could not be afforded.
 */
export function guestPost(delta: number, reason: GuestReason): GuestWallet | null {
  const w = readGuestWallet();
  const d = Math.trunc(delta);
  if (w.balance + d < 0) return null;
  const next: GuestWallet = {
    balance: w.balance + d,
    ledger: [{ delta: d, balanceAfter: w.balance + d, reason, at: Date.now() }, ...w.ledger].slice(0, KEEP),
  };
  write(next);
  return next;
}

/** The Barkeep, device-side. The same floor, the same 1,000, the same lack of a cap. */
export function guestStake(): GuestWallet | null {
  const w = readGuestWallet();
  if (w.balance >= STAKE_FLOOR) return null;
  return guestPost(STAKE_TO - w.balance, "stake");
}

/**
 * Cleared on first sign-in, and the copy on screen says so before it happens.
 *
 * See the header: the device balance is not carried across, because a balance a person
 * can manufacture by signing up again is not a balance anything can be ranked on.
 */
export function clearGuestWallet(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do about it */ }
}

/* ---------------------------------------------------------- what a seat costs --- */

/** The shape of a table, as far as the wallet is concerned. */
export interface SeatShape {
  format: "cash" | "tournament";
  seats: number;
  bigBlind: number;
  stackBb: number;
  /** A tournament card names its buy-in; a cash card's is its starting stack. */
  buyin?: number;
}

/**
 * What sitting down costs, in Bucks.
 *
 * A CASH seat costs its starting stack, because at a cash table a chip is a Buck: 100
 * big blinds at 1/2 is 200 Bucks, at 2/5 it is 500, and the arithmetic is the whole of
 * the conversion. Custom Table shows it live for exactly that reason — the number is
 * derived from the dials, never typed in beside them, so the two cannot disagree.
 *
 * A TOURNAMENT seat costs the buy-in the card names, and its chips are not Bucks.
 */
export function seatCost(t: SeatShape): number {
  if (t.format === "tournament") return Math.max(0, Math.round(t.buyin ?? 0));
  return Math.max(0, Math.round(t.bigBlind * t.stackBb));
}

/** "1/2 · 100bb" — the stakes a cash card names, in the units of the table. */
export function stakesLabel(t: SeatShape): string {
  const small = t.bigBlind / 2;
  const n = (v: number) => (Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100));
  return `${n(small)}/${n(t.bigBlind)} · ${t.stackBb}bb`;
}

/**
 * What a tournament buy-in funds.
 *
 * Every seat, and nothing else. These tournaments have no real prize — the scheduled
 * ones in migration 0006 do, and those are free to enter and take no Bucks at all —
 * so a Bucks buy-in may fund a Bucks pool and the sentence on the card says which
 * kind of tournament this is.
 */
export function prizePool(t: SeatShape): number {
  return seatCost(t) * t.seats;
}

/**
 * ============ CAN A POKER SITTING TAKE A SECOND BUY-IN? YES, SINCE PHASE 6. ====
 * PHASE 6, BOX 0(1): the Worker line below was written and is now in
 * `app/src/routes/game.ts`, so this is `true` and the busted-hero card offers the
 * second buy-in. The argument that kept it shut is kept verbatim underneath, because
 * it is the reason the Worker line has to stay the way it is; `startChipsFor` under
 * it is the client's half of the same formula, and `tests/phase5-copy.test.ts` fails
 * the day the two part company. **The Worker must be redeployed for this to be true
 * of a running preview** — the flag is client-side and the arithmetic is not.
 *
 * ---------------------------- the original note --------------------------------
 * Phase 5 box 4 asks a busted hero at a cash table to be offered a rebuy — a second
 * `buyin` posting on the same nonce, which is the shape Phase 4 built and which
 * blackjack has used since. The card, the pause, the decline and the Ledger path are
 * all built. This constant is `false` because the WALLET cannot price the result yet,
 * and the arithmetic is short enough to write out:
 *
 *   `bucksOut(buyin, startChips, net, maxOut)` = buyin x (startChips + net)/startChips
 *
 * `buyin` is CUMULATIVE across rebuys — that is what "the same nonce" means — and
 * `startChips` is what one seat bought. For poker the Worker supplies
 * `startChips: () => stackBb * bigBlind`, ignoring the buy-in it is handed, so the two
 * stop describing the same table the moment there are two buy-ins:
 *
 *   | sitting                        | paid | chips held | server pays |
 *   | one seat, lost the lot         |  200 |          0 |           0 |
 *   | one seat, doubled              |  200 |        400 |         400 |
 *   | rebought, then broke even      |  400 |        200 |           0 |
 *   | rebought, then up a hundred    |  400 |        500 |         600 |
 *   | rebought, then down a hundred  |  400 |        300 |         200 |
 *
 * Wrong in both directions, and the client cannot correct it: the only inputs it
 * chooses are `stackBb` and `bigBlind`, which describe the table and must stay true,
 * and `net`, which is the leak report. Blackjack is unaffected — its own line is
 * `startChips: (buyin) => buyin / chipValue`, which scales with the rebuy, which is
 * why nobody noticed. Poker simply never had a rebuy before this phase.
 *
 * The fix is one line in `app/src/routes/game.ts`, in a file Phase 5 may not open:
 *
 *     startChips: (buyin) => (v.format === "cash" ? buyin : v.stackBb * v.bigBlind),
 *
 * — exact for a cash table (a chip is a Buck, so the chips bought ARE the Bucks paid),
 * unchanged for a tournament (which does not rebuy), and identical to today's value on
 * every sitting with one buy-in. `tests/wallet-client.test.ts` watches that line and
 * fails the day it changes without this flag changing with it.
 *
 * Until then a busted hero is shown the Ledger rather than a control that would settle
 * their Bucks wrongly. Both cards are built and both are in the Phase 5 screenshots.
 * ===============================================================================
 */
export const REBUY_TAKES_A_SECOND_BUYIN = true;

/**
 * ============== PHASE 8: THE BLACKJACK BET STEPPER, AND WHY IT IS HELD ==========
 * The stepper is built — 25 · 50 · 100 · 200 within the rack, locked mid-hand, the
 * engine dealt at the chosen bet (`startHand(bet)`), a double or split costing the
 * hand's own bet, insurance half of it, the verdict and the Ledger's EV priced at it,
 * and a driven sitting proved equal to an undriven one at every one of those bets.
 * What it cannot do yet is SETTLE, and the reason is one line in a file this phase
 * may not open.
 *
 * `validateBlackjack` in `app/src/routes/game.ts` bounds a sitting's `net` at
 * `4 × hands + 4` bet units and its EV given up at `2 × decisions`, and `maxOut` caps
 * what a rack can come back as by the same swing — which is exactly right at one
 * 25-Buck chip a hand and is wrong the moment a hand can be 200: two hands lost at 200
 * are −16 units against a bound of 12, the summary is refused, and a player holding a
 * second rack's 400 Bucks is never paid them. A guest's device settlement has the same
 * cap. So while this is `false` the stepper shows the one stop that settles exactly
 * and says so in one line, and the "no bet advice" promise on the page stays true.
 *
 * The fix, for the planning session (PHASE8-REPORT, question 1): the client sends the
 * sitting's total opening bet in units and the Worker's swing becomes
 * `4 × betUnits + 4` (and EV `2 × betUnits`), with `maxOut` following it. Then this
 * flips and nothing else in the room changes.
 * ================================================================================
 */
export const BJ_STEPPER_SETTLES = true;

/**
 * PHASE 9 — OPENED. The Worker now takes `betUnits`, the sitting's opening bets summed
 * in 25-Buck units, and bounds the swing at `4 × betUnits + 4` (`bjSwing` in
 * `app/src/routes/game.ts`, which also refuses a `betUnits` below one a hand or above
 * the stepper's 8). `evGiven` stays per unit of each hand's own bet, so its `2 ×
 * decisions` ceiling was never the problem. The room counts `betUnits` at the deal.
 *
 * This is the client's copy of the Worker's cap on what a rack can come back as, used
 * for the `stackOut` cross-check and for a guest's settlement on this device. Two copies
 * of one rule, like `settleAmount`, and `tests/phase9-stepper.test.ts` holds them equal.
 */
export const BJ_MAX_UNITS_PER_HAND = 8;
export function bjMaxOut(buyin: number, betUnits: number, chipValue: number): number {
  return buyin + Math.max(4, betUnits * 4 + 4) * chipValue;
}

/**
 * How many chips a poker buy-in bought — the client's copy of the Worker's own line.
 *
 * There are two copies of this rule for the same reason there are two copies of
 * `settleAmount`: the client sends `stackOut` as a cross-check and the server refuses
 * a settlement that disagrees with what it derives by more than a Buck, so a client
 * that computed the ratio differently would simply stop being able to stand up.
 *
 * At a CASH table a chip is a Buck, so the chips bought are the Bucks paid — and
 * `buyin` is CUMULATIVE across rebuys, which is the whole point of the fix. At a
 * tournament the chips were never Bucks; the seat buys a starting stack.
 */
export function startChipsFor(
  t: { format: "cash" | "tournament"; stackBb: number; bigBlind: number }, buyin: number,
): number {
  return t.format === "cash" ? buyin : t.stackBb * t.bigBlind;
}

/**
 * What a sitting is worth when the player stands up, in Bucks.
 *
 * THE SAME ARITHMETIC AS THE SERVER'S `bucksOut` in `src/routes/game.ts`, and it has
 * to be: the client sends this number as `stackOut` and the server refuses a
 * settlement that disagrees with what it derives itself by more than a Buck. Two
 * copies of one formula is a thing that rots, so `tests/wallet-client.test.ts` runs
 * both over a few hundred random tables and fails the day they part company.
 *
 * The buy-in bought `startChips` chips, so a chip is worth `buyin / startChips` Bucks.
 * At a cash table that ratio is one. Floored at zero — the buy-in is the whole
 * exposure — and capped by `maxOut`, which for a poker table is everything that was on
 * it.
 */
export function settleAmount(
  buyin: number, startChips: number, net: number, maxOut: number,
): number | null {
  if (!(startChips > 0) || !(buyin > 0) || !Number.isFinite(net) || !Number.isFinite(maxOut)) return null;
  const raw = Math.round(buyin * ((startChips + net) / startChips));
  return Math.max(0, Math.min(raw, Math.round(maxOut)));
}

/**
 * PHASE 8: a blackjack hand still on the table when the sitting settles.
 *
 * A sitting's `net` reaches the room only at `hand_over`, a second or three after the
 * engine has already settled the hand. Leave is off mid-hand, but the back link, the
 * phone's back button and closing the tab are not, and each settles from the running
 * total — so a player who watched the hole card turn against them could step out
 * before the chips moved and keep the stake. This is what the settlement adds for that
 * hand: its real result once the engine has settled it, and the loss of everything in
 * the circle while it is still waiting on the player — which is what walking away from
 * a table does. Nothing, for a hand already counted or no hand at all.
 */
export function bjOpenHand(
  t: { handNumber: number; phase: string; net(seat: number): number;
       seats: readonly { hands: readonly { bet: number }[]; insurance: number }[] },
  counted: number,
): { hands: number; net: number } {
  if (t.handNumber === 0 || counted === t.handNumber) return { hands: 0, net: 0 };
  const hero = t.seats[0]!;
  const net = t.phase === "settled"
    ? t.net(0)
    : -(hero.hands.reduce((n, h) => n + h.bet, 0) + hero.insurance);
  return { hands: 1, net };
}
